import { createSlice, createEntityAdapter } from "@reduxjs/toolkit";

const messageTemplateAdapter = createEntityAdapter({
  selectId: (template) => template._id,
});

const initialState = messageTemplateAdapter.getInitialState({
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  searchLoading: false,
  error: null,
  totalDocs: 0,
  page: 1,
  limit: 10,
  totalPages: 0,
  hasNextPage: false,
  hasPrevPage: false,
  searchResults: [],
  activeTemplates: [],
  categoryTemplates: [],
  selectedTemplate: null,
  filters: {
    category: null,
    isActive: null,
    searchQuery: null,
  },
});

const messageTemplateSlice = createSlice({
  name: "messageTemplate",
  initialState,
  reducers: {
    // Get all templates
    getMessageTemplatesRequest: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    getMessageTemplatesSuccess: (state, action) => {
      state.loading = false;
      const { docs, totalDocs, page, limit, totalPages, hasNextPage, hasPrevPage } = action.payload;
      messageTemplateAdapter.setAll(state, docs);
      state.totalDocs = totalDocs;
      state.page = page;
      state.limit = limit;
      state.totalPages = totalPages;
      state.hasNextPage = hasNextPage;
      state.hasPrevPage = hasPrevPage;
    },
    getMessageTemplatesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Get template by ID
    getMessageTemplateByIdRequest: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    getMessageTemplateByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedTemplate = action.payload;
      messageTemplateAdapter.upsertOne(state, action.payload);
    },
    getMessageTemplateByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Create template
    createMessageTemplateRequest: (state, action) => {
      state.creating = true;
      state.error = null;
    },
    createMessageTemplateSuccess: (state, action) => {
      state.creating = false;
      messageTemplateAdapter.addOne(state, action.payload);
    },
    createMessageTemplateFailure: (state, action) => {
      state.creating = false;
      state.error = action.payload;
    },

    // Update template
    updateMessageTemplateRequest: (state, action) => {
      state.updating = true;
      state.error = null;
    },
    updateMessageTemplateSuccess: (state, action) => {
      state.updating = false;
      messageTemplateAdapter.upsertOne(state, action.payload);
    },
    updateMessageTemplateFailure: (state, action) => {
      state.updating = false;
      state.error = action.payload;
    },

    // Delete template
    deleteMessageTemplateRequest: (state, action) => {
      state.deleting = true;
      state.error = null;
    },
    deleteMessageTemplateSuccess: (state, action) => {
      state.deleting = false;
      messageTemplateAdapter.removeOne(state, action.payload.id);
    },
    deleteMessageTemplateFailure: (state, action) => {
      state.deleting = false;
      state.error = action.payload;
    },

    // Get active templates
    getActiveTemplatesRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    getActiveTemplatesSuccess: (state, action) => {
      state.loading = false;
      state.activeTemplates = action.payload;
    },
    getActiveTemplatesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Get templates by category
    getTemplatesByCategoryRequest: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    getTemplatesByCategorySuccess: (state, action) => {
      state.loading = false;
      state.categoryTemplates = action.payload;
    },
    getTemplatesByCategoryFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Search templates
    searchTemplatesRequest: (state, action) => {
      state.searchLoading = true;
      state.error = null;
    },
    searchTemplatesSuccess: (state, action) => {
      state.searchLoading = false;
      state.searchResults = action.payload;
    },
    searchTemplatesFailure: (state, action) => {
      state.searchLoading = false;
      state.error = action.payload;
    },

    // Set filters
    setTemplateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Clear filters
    clearTemplateFilters: (state) => {
      state.filters = initialState.filters;
    },

    // Clear error
    clearTemplateError: (state) => {
      state.error = null;
    },

    // Reset state
    resetTemplateState: () => initialState,
  },
});

export const {
  getMessageTemplatesRequest,
  getMessageTemplatesSuccess,
  getMessageTemplatesFailure,
  getMessageTemplateByIdRequest,
  getMessageTemplateByIdSuccess,
  getMessageTemplateByIdFailure,
  createMessageTemplateRequest,
  createMessageTemplateSuccess,
  createMessageTemplateFailure,
  updateMessageTemplateRequest,
  updateMessageTemplateSuccess,
  updateMessageTemplateFailure,
  deleteMessageTemplateRequest,
  deleteMessageTemplateSuccess,
  deleteMessageTemplateFailure,
  getActiveTemplatesRequest,
  getActiveTemplatesSuccess,
  getActiveTemplatesFailure,
  getTemplatesByCategoryRequest,
  getTemplatesByCategorySuccess,
  getTemplatesByCategoryFailure,
  searchTemplatesRequest,
  searchTemplatesSuccess,
  searchTemplatesFailure,
  setTemplateFilters,
  clearTemplateFilters,
  clearTemplateError,
  resetTemplateState,
} = messageTemplateSlice.actions;

export const messageTemplateSelectors = messageTemplateAdapter.getSelectors(
  (state) => state.messageTemplate
);

export default messageTemplateSlice.reducer;